import { Link } from "@tanstack/react-router";
import { GraduationCap, Instagram } from "lucide-react";
import reflective from "@/assets/dr-sara-reflective-arch.jpg.asset.json";
import { DEGREES } from "@/data/credentials";
import { SITE_FIGURES } from "@/data/site-figures";
import { CHANNEL } from "@/data/home-videos";
import { DOCTORATE_LINE } from "@/components/home/situation-picker";

/** The Instagram follower figure, read from the shared figures list. */
const INSTAGRAM_FIGURE = SITE_FIGURES.find(([value]) => value === "1M+");

/** Where the three pills sit on the arch; positions differ a little per size. */
type PillSpots = { youtube: string; instagram: string; doctorate: string };

const MOBILE_SPOTS: PillSpots = {
  youtube: "-start-2 top-10",
  instagram: "-end-2 bottom-[3.75rem]",
  doctorate: "-end-2 bottom-5",
};

const DESKTOP_SPOTS: PillSpots = {
  youtube: "-start-2 top-12",
  instagram: "-end-2 bottom-[4.25rem]",
  doctorate: "-end-2 bottom-6",
};

/** YouTube / Instagram / doctorate pills stacked over the arch photo. */
function ArchPills({ spots }: { spots: PillSpots }) {
  return (
    <>
      <p className={`absolute ${spots.youtube} rounded-ds-pill bg-surface px-3.5 py-1.5 font-display text-[13px] font-bold text-text-primary shadow-md`}>
        <bdi dir="ltr">{Math.round(CHANNEL.subscribers / 1000)}</bdi> ألف مشترك على يوتيوب
      </p>
      {INSTAGRAM_FIGURE && (
        <p className={`absolute ${spots.instagram} inline-flex items-center gap-1.5 rounded-ds-pill bg-surface px-3.5 py-1.5 font-display text-[13px] font-bold text-text-primary shadow-md`}>
          <Instagram className="h-4 w-4 text-maroon-500" strokeWidth={1.75} aria-hidden />
          <bdi dir="ltr">{INSTAGRAM_FIGURE[0]}</bdi> {INSTAGRAM_FIGURE[1]}
        </p>
      )}
      <p className={`absolute ${spots.doctorate} inline-flex items-center gap-1.5 rounded-ds-pill bg-teal-700 px-3.5 py-1.5 font-display text-[13px] font-bold text-cream-50 shadow-md`}>
        <GraduationCap className="h-4 w-4" strokeWidth={1.75} aria-hidden />
        {DOCTORATE_LINE}
      </p>
    </>
  );
}


/** «السلوك ليس مشكلة.. السلوك رسالة» — arch photo beside the quote, degrees as tiles. */
export function DrSaraMessage({ duration }: { duration: number | string }) {
  return (
    <div className="reveal mx-auto grid max-w-[76rem] gap-10 px-5 py-16 lg:grid-cols-[340px_minmax(0,1fr)] lg:items-center lg:gap-16 lg:py-24">
      {/* Mobile arch */}
      <div className="relative mx-auto w-full max-w-[300px] lg:hidden">
        <div className="overflow-hidden rounded-b-ds-xl rounded-t-[150px] bg-maroon-100">
          <img
            src={reflective.url}
            alt="د. سارة النحّاس"
            width={580}
            height={725}
            loading="lazy" decoding="async"
            className="h-[330px] w-full object-cover object-center"
          />
        </div>
        <ArchPills spots={MOBILE_SPOTS} />

      </div>
      {/* Desktop arch */}
      <div className="relative hidden lg:block">
        <div className="overflow-hidden rounded-b-ds-xl rounded-t-[170px] bg-maroon-100">
          <img
            src={reflective.url}
            alt="د. سارة النحّاس"
            width={580}
            height={725}
            loading="lazy" decoding="async"
            className="h-[420px] w-[340px] object-cover object-center"
          />
        </div>
        <ArchPills spots={DESKTOP_SPOTS} />
      </div>


      <div className="min-w-0">
        <span aria-hidden className="block font-display text-[88px] font-extrabold leading-[0.6] text-amber-200 lg:text-[120px]">
          «
        </span>
        <p className="mt-4 font-display text-[30px] font-extrabold leading-[1.35] text-maroon-500 lg:text-[44px]">
          السلوك ليس مشكلة.. <span className="mark">السلوك رسالة</span>
        </p>
        <p className="mt-5 text-[17px] leading-[1.85] text-text-secondary lg:text-[19px]">
          هون ما بنصحّح تربيتك. بنساعدك على الهدوء والثقة اللي بدّك ياها مع طفلك — بمقال، أو ببوصلة، أو بجلسة {duration} دقيقة.
        </p>
        <ul className="mt-7 grid gap-3 sm:grid-cols-3">
          {DEGREES.map((d) => (
            <li key={d.id} className="rounded-ds-lg border border-hairline-subtle bg-surface px-4 py-4 shadow-sm">
              <p dir="ltr" className="text-end font-display text-[22px] font-extrabold leading-none text-amber-800">{d.year}</p>
              <p className="mt-2 font-display text-[14px] font-bold leading-snug text-text-primary">{d.title}</p>
            </li>
          ))}
        </ul>
        <Link
          to="/about"
          hash="credentials"
          className="mt-4 inline-flex min-h-11 items-center font-display text-[15px] font-bold text-text-link underline-offset-4 hover:underline"
        >
          كل المؤهلات والشهادات ←
        </Link>
      </div>
    </div>
  );
}
